/**
 * Duplicate of events/utils/ticket-type-utils: same functions (isOnSaleTicketType,
 * isTicketTypeWaitlisted, isTicketTypeSoldOut, ticketDisplayLowInventory, voucher*,
 * cleanTicketType). Callers are split between this file and events. isUpcomingSaleTicketType
 * here is unused (callers use the events version). Consolidate to one module when possible.
 */
import { UserBasedDiscountTicketInfoSerializer } from "@shared/modules/discounts/types";
import {
  PublicCalendarTicketTypeSerializer,
  TicketTypeSerializer,
} from "@shared/modules/events/types/public";
import { SeatingItemPropertyMap } from "@shared/modules/seating/types/seating-property-types";
import {
  isDateAfter,
  isDateBefore,
} from "@shared/utils/dates/comparisons/comparisons";

// Same as events/utils/ticket-type-utils.isUpcomingSaleTicketType. Not used (callers use the events version).
/**
 * Returns true if the ticket type's sale end time is in the future
 * @param {object} ticketType
 * @return {boolean}
 */
export const isUpcomingSaleTicketType = (
  ticketType: TicketTypeSerializer
): boolean => {
  if (!ticketType.sale_ends_on) return false;
  return isDateAfter(ticketType.sale_ends_on, new Date());
};

/**
 * @param ticketType the ticket type to check
 * @param allowStartsOnBypass sale is allowed before starts on date
 * @return {boolean} Whether or not ticket type is currently available on sale
 */
export const isOnSaleTicketType = (
  ticketType: TicketTypeSerializer | SeatingItemPropertyMap,
  allowStartsOnBypass?: boolean
): boolean => {
  const now = new Date();
  if (
    allowStartsOnBypass &&
    ticketType.bypass_sale_starts_on_box_office &&
    ticketType.sale_starts_on &&
    isDateAfter(ticketType.sale_starts_on, now)
  ) {
    return true;
  }
  return (
    isDateBefore(ticketType.sale_starts_on, now) &&
    isDateAfter(ticketType.sale_ends_on, now)
  );
};

/**
 * True if waitlist is truthy and the status is = "active"
 * @param tt ticket type object
 * @returns {boolean}
 */
export const isTicketTypeWaitlisted = (
  tt?: TicketTypeSerializer | SeatingItemPropertyMap | null
): boolean => {
  return Boolean(tt?.waitlist && tt.waitlist.status === "active");
};

/**
 * True if unavailable
 *
 * @param tt ticket type object
 * @return {boolean|*}
 */
export const isTicketTypeSoldOut = (
  tt:
    | TicketTypeSerializer
    | SeatingItemPropertyMap
    | PublicCalendarTicketTypeSerializer
): boolean => {
  // TODO: implement seating map logic with sold out tickets

  // if (selectedPermissionsContainGA() && !tt.sold_out) {
  //   const permissions = locationPermissions.filter((lp) => lp.ticket_type === tt.id);
  //   return permissions.every((e) => e.stats && e.stats.fully_claimed === true);
  // }

  if (
    "is_available_for_quantity_requested" in tt &&
    (tt as PublicCalendarTicketTypeSerializer)
      .is_available_for_quantity_requested === false
  ) {
    return true;
  }

  return tt.sold_out;
};

type InventoryStatusType = {
  displayValue: boolean;
  inventoryValue: number | null | undefined;
};

/**
 * Displays or hides the LowInventoryStatus/RemainingTicketStatus components, based on the return value
 * Also gets the remaining amount of ticket inventory
 * @param tts ticket type object
 * @return {InventoryStatusType}
 */
export const ticketDisplayLowInventory = (
  tts: TicketTypeSerializer | SeatingItemPropertyMap
): InventoryStatusType => {
  const totalAmountLeft = tts.inventory_left;
  const showRemainingTickets = Boolean(totalAmountLeft);

  const returnValue = {
    displayValue: showRemainingTickets,
    inventoryValue: totalAmountLeft,
  };

  return returnValue;
};

// returns a single applicable voucher for the input ticket type
export const voucherForTT = (
  type: TicketTypeSerializer | SeatingItemPropertyMap,
  userVouchers: UserBasedDiscountTicketInfoSerializer[]
): UserBasedDiscountTicketInfoSerializer | undefined => {
  return userVouchers?.find((voucher) =>
    voucher.$applicableTTs?.includes(type.id)
  );
};

// returns an applicable voucher code for the input ticket type
export const voucherCodeForTT = (
  type: TicketTypeSerializer | SeatingItemPropertyMap,
  userVouchers: UserBasedDiscountTicketInfoSerializer[]
): string | null => {
  const voucher = voucherForTT(type, userVouchers);

  if (voucher) {
    return voucher.code;
  }

  return null;
};

// returns a list of applicable vouchers for the input ticket type
export const voucherListForTT = (
  type: TicketTypeSerializer | SeatingItemPropertyMap,
  userVouchers: UserBasedDiscountTicketInfoSerializer[]
): UserBasedDiscountTicketInfoSerializer[] => {
  return userVouchers.filter((voucher) =>
    voucher.$applicableTTs?.includes(type.id)
  );
};

// returns true if no voucher is required to buy the input ticket type
// else returns a boolean value based on whether the user has an applicable voucher for the ticket type
export const voucherNotRequired = (
  type: TicketTypeSerializer | SeatingItemPropertyMap,
  userVouchers: UserBasedDiscountTicketInfoSerializer[]
): boolean => {
  // Block the ticket type from being added to cart if
  // TT requires a voucher and user has no voucher for the TT
  if (type.voucher_purchases_only) {
    if (userVouchers) {
      return userVouchers.some(
        (voucher: UserBasedDiscountTicketInfoSerializer) =>
          voucher.$applicableTTs?.includes(type.id)
      );
    }
    // else return false
    return false;
  }
  return true;
};

export const cleanTicketType = (
  ticketType: TicketTypeSerializer,
  isDynamicHoldTicketType?: boolean
): TicketTypeSerializer => {
  const soldOutObj = {
    sold_out: ticketType.sold_out && !isDynamicHoldTicketType,
  };
  Object.assign(ticketType, soldOutObj);
  return ticketType;
};
